import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, FileCheck2Icon } from "lucide-react";
import { cn } from "~/lib/utils";
import { findingStatusPill } from "../finding/findingView";
import { suggestionStatusPill } from "../policy/suggestionsView";
import type { InspectionItems } from "./inspectorView";

export function InspectionResults({
  items,
  orgSegment,
  completed,
}: {
  items: InspectionItems;
  orgSegment: string;
  completed: boolean;
}) {
  const total =
    items.findings.length +
    items.suggestions.length +
    items.publications.length +
    items.activity.length;
  if (total === 0)
    return completed ? (
      <div className="flex items-start gap-3 rounded-xl border border-border/70 p-4">
        <FileCheck2Icon aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          No findings, suggestions or publications were recorded during this run.
        </p>
      </div>
    ) : null;
  return (
    <section aria-label="Inspection results" className="space-y-5">
      <div>
        <h2 className="text-sm font-semibold">Recorded during this run</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Open an item to review the evidence and decide what to do.
        </p>
      </div>
      {items.findings.length > 0 ? (
        <section>
          <h3 className="mb-2 text-xs font-medium text-muted-foreground">
            Findings · {items.findings.length}
          </h3>
          <ul className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/70">
            {items.findings.map((item) => {
              const pill = findingStatusPill(item.status);
              return (
                <li key={item.id}>
                  <Link
                    to="/$org/compliance/findings/$findingId"
                    params={{ org: orgSegment, findingId: item.id }}
                    className="flex items-start gap-3 px-4 py-3 hover:bg-muted/40"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">{item.title}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {item.policyName ?? "Document"}
                      </span>
                    </span>
                    <span
                      className={cn(
                        "shrink-0 rounded-full px-2 py-0.5 text-[11px]",
                        pill.className,
                      )}
                    >
                      {pill.label}
                    </span>
                    <ArrowUpRightIcon
                      aria-hidden
                      className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
      {items.suggestions.length > 0 ? (
        <section>
          <h3 className="mb-2 text-xs font-medium text-muted-foreground">
            Suggestions · {items.suggestions.length}
          </h3>
          <ul className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/70">
            {items.suggestions.map((item) => {
              const pill = suggestionStatusPill(item.status);
              return (
                <li key={item.id}>
                  <Link
                    to="/$org/compliance/suggestions/$recommendationId"
                    params={{ org: orgSegment, recommendationId: item.id }}
                    className="flex items-start gap-3 px-4 py-3 hover:bg-muted/40"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">{item.title}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {item.policyName ?? "The library"}
                      </span>
                    </span>
                    <span
                      className={cn(
                        "shrink-0 rounded-full px-2 py-0.5 text-[11px]",
                        pill.className,
                      )}
                    >
                      {pill.label}
                    </span>
                    <ArrowUpRightIcon
                      aria-hidden
                      className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
      {items.publications.length > 0 ? (
        <section>
          <h3 className="mb-2 text-xs font-medium text-muted-foreground">
            Publications · {items.publications.length}
          </h3>
          <ul className="divide-y divide-border/60 rounded-xl border border-border/70">
            {items.publications.map((item) => (
              <li key={item.id} className="px-4 py-3 text-sm">
                {item.title}
              </li>
            ))}
          </ul>
          <Link
            to="/$org/compliance/regulatory"
            params={{ org: orgSegment }}
            className="mt-2 inline-block text-xs font-medium text-primary hover:underline"
          >
            Open regulatory updates
          </Link>
        </section>
      ) : null}
      {items.activity.length > 0 ? (
        <section>
          <h3 className="mb-2 text-xs font-medium text-muted-foreground">
            Revisions and withdrawals
          </h3>
          <ul className="space-y-2">
            {items.activity.map((item, index) => (
              <li
                key={`${item.createdAt}:${index}`}
                className="rounded-lg bg-muted/40 px-4 py-3 text-sm"
              >
                {item.detail}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </section>
  );
}
